import React, {useMemo, useState} from 'react'
import {useIntl} from 'react-intl'

import {
  AttendanceV2Payload,
  ComprehensiveReportData,
  ReportRollupRow,
  getComprehensiveReportData,
} from '../../../../modes/attendance/attendanceV2Types'
import AttendancePlainCard from '../AttendancePlainCard'
import {ColumnDef} from './AttendanceV2DataTable'
import AttendanceV2ListSection from './AttendanceV2ListSection'

type AttendanceV2ComprehensiveReportViewProps = {
  payload: AttendanceV2Payload
  theme: 'light' | 'dark'
}

/** Whatever the backend chose to group by. Not a closed set — pinning it to the axes that existed
 *  when this was written is what made a `campus` roll-up render as an empty card. */
type RollupAxis = string

type AxisTab = {
  axis: RollupAxis
  label: string
  columns: ColumnDef[]
  rows: Record<string, unknown>[]
}

function formatPercent(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
  return `${Math.round(value * 10) / 10}%`
}

const PERCENT_COLUMN: ColumnDef = {
  key: 'attendance_percentage',
  label: 'Attendance',
  format: formatPercent,
}

const STUDENT_COUNT_COLUMN: ColumnDef = {key: 'student_count', label: 'Students'}

const AXIS_LABEL_IDS: Record<string, string> = {
  grade: 'CHATBOT.ATTENDANCE.REPORT_BY_GRADE',
  section: 'CHATBOT.ATTENDANCE.REPORT_BY_SECTION',
  className: 'CHATBOT.ATTENDANCE.REPORT_BY_CLASS',
  gender: 'CHATBOT.ATTENDANCE.REPORT_BY_GENDER',
  campus: 'CHATBOT.ATTENDANCE.REPORT_BY_CAMPUS',
}

const AXIS_COLUMN_LABELS: Record<string, string> = {
  grade: 'Grade',
  section: 'Section',
  className: 'Class',
  gender: 'Gender',
  campus: 'Branch',
}

/** Tab order for the axes we know; anything new the backend sends follows, in payload order. */
const KNOWN_AXIS_ORDER = ['grade', 'section', 'className', 'gender', 'campus']

function humanizeAxis(axis: string): string {
  const spaced = axis
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const LOWEST_CLASS_COLUMNS: ColumnDef[] = [
  {key: 'className', label: 'Class'},
  {key: 'gradeName', label: 'Grade'},
  {key: 'sectionName', label: 'Section'},
  PERCENT_COLUMN,
]

const BELOW_THRESHOLD_COLUMNS: ColumnDef[] = [
  {key: 'studentName', label: 'Student'},
  {key: 'grade', label: 'Grade'},
  {key: 'section', label: 'Section'},
  PERCENT_COLUMN,
]

/** A roll-up bucket is only worth a tab if at least one row names its axis. Axes are read off the
 *  payload rather than hard-coded, so a grouping the backend adds later still renders. */
function buildAxisTabs(
  data: ComprehensiveReportData,
  formatMessage: (descriptor: {id: string}) => string
): AxisTab[] {
  const rollups = data.rollups ?? {}
  const axes = [
    ...KNOWN_AXIS_ORDER.filter((axis) => axis in rollups),
    ...Object.keys(rollups).filter((axis) => !KNOWN_AXIS_ORDER.includes(axis)),
  ]

  const tabs = axes.flatMap<AxisTab>((axis) => {
    const rows = (rollups[axis] ?? []).filter(
      (row: ReportRollupRow) => String(row?.[axis] ?? '').trim() !== ''
    )
    if (rows.length === 0) return []

    const labelId = AXIS_LABEL_IDS[axis]
    return [
      {
        axis,
        label: labelId ? formatMessage({id: labelId}) : `By ${humanizeAxis(axis).toLowerCase()}`,
        columns: [
          {key: axis, label: AXIS_COLUMN_LABELS[axis] ?? humanizeAxis(axis)},
          STUDENT_COUNT_COLUMN,
          PERCENT_COLUMN,
        ],
        rows: rows as Record<string, unknown>[],
      },
    ]
  })

  // A one-row axis sitting beside richer ones is noise — a lone `{gender: 'Other'}` bucket covering
  // the whole school says nothing the other tabs don't. But when every axis is one row it IS the
  // report: a single-branch campus roll-up used to be dropped, leaving a card with just a date.
  const multiRow = tabs.filter((tab) => tab.rows.length >= 2)
  return multiRow.length > 0 ? multiRow : tabs
}

/** Renders a `comprehensive_report` payload as a scannable card — headline KPIs, one table per
 *  breakdown axis behind tabs, and the two attention lists — instead of the paragraph-long
 *  statistics dump the backend also emits in the narrative. The generic result table can't read
 *  any of it because the numbers live under `rollups`/`lowestClasses`/`belowThresholdStudents`
 *  rather than the canonical `rows`. */
const AttendanceV2ComprehensiveReportView: React.FC<AttendanceV2ComprehensiveReportViewProps> = ({
  payload,
  theme,
}) => {
  const intl = useIntl()
  const data = useMemo(() => getComprehensiveReportData(payload), [payload])
  const tabs = useMemo(
    () => (data ? buildAxisTabs(data, intl.formatMessage) : []),
    [data, intl.formatMessage]
  )
  const [activeAxis, setActiveAxis] = useState<RollupAxis | null>(null)

  if (!data) return null

  const activeTab = tabs.find((tab) => tab.axis === activeAxis) ?? tabs[0]
  const current = data.comparison?.rangeA
  const previous = data.comparison?.rangeB
  const threshold = data.threshold ?? data.belowThreshold
  const belowThresholdStudents = data.belowThresholdStudents ?? []
  const lowestClasses = data.lowestClasses ?? []

  // Every axis partitions the same population, so any populated one totals it. Keep the old
  // gender/section preference, then fall back to whatever axis the payload does carry.
  const countSource =
    [data.rollups?.gender, data.rollups?.section, ...Object.values(data.rollups ?? {})].find(
      (rows) => Array.isArray(rows) && rows.length > 0
    ) ?? []

  const totalStudents = countSource.reduce(
    (sum, row) => sum + (typeof row.student_count === 'number' ? row.student_count : 0),
    0
  )

  const delta =
    typeof current?.attendance_percentage === 'number' &&
    typeof previous?.attendance_percentage === 'number'
      ? current.attendance_percentage - previous.attendance_percentage
      : null

  const kpiPairs: {metric: string; value: string}[] = []
  if (current?.attendance_percentage != null) {
    kpiPairs.push({
      metric: intl.formatMessage({id: 'CHATBOT.ATTENDANCE.REPORT_OVERALL'}),
      value: formatPercent(current.attendance_percentage),
    })
  }
  if (previous?.attendance_percentage != null) {
    kpiPairs.push({
      metric: intl.formatMessage({id: 'CHATBOT.ATTENDANCE.REPORT_PREVIOUS'}),
      value: formatPercent(previous.attendance_percentage),
    })
  }
  if (delta != null) {
    const rounded = Math.round(delta * 10) / 10
    kpiPairs.push({
      metric: intl.formatMessage({id: 'CHATBOT.ATTENDANCE.REPORT_CHANGE'}),
      value: `${rounded > 0 ? '+' : ''}${rounded} pts`,
    })
  }
  if (totalStudents > 0) {
    kpiPairs.push({
      metric: intl.formatMessage({id: 'CHATBOT.ATTENDANCE.REPORT_STUDENTS'}),
      value: String(totalStudents),
    })
  }
  if (threshold != null && belowThresholdStudents.length > 0) {
    kpiPairs.push({
      metric: intl.formatMessage(
        {id: 'CHATBOT.ATTENDANCE.REPORT_BELOW_THRESHOLD_KPI'},
        {threshold}
      ),
      value: String(belowThresholdStudents.length),
    })
  }

  const dateRange =
    data.from && data.to ? `${data.from} – ${data.to}` : data.from || data.to || null

  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
  const cardBg = theme === 'dark' ? 'rgba(15, 23, 42, 0.94)' : 'rgba(255,255,255,0.65)'
  const mutedColor = theme === 'dark' ? 'rgba(255,255,255,0.75)' : '#6c757d'
  const activeTabBg = theme === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.07)'
  const activeTabColor = theme === 'dark' ? '#ffffff' : '#212529'

  return (
    <div
      className='attendance-v2-result mt-2 mb-1 rounded-3 p-2'
      style={{border: `1px solid ${borderColor}`, backgroundColor: cardBg}}
    >
      {dateRange ? (
        <div className='mb-2' style={{fontSize: '0.75rem', color: mutedColor}}>
          {dateRange}
        </div>
      ) : null}

      {kpiPairs.length ? <AttendancePlainCard pairs={kpiPairs} theme={theme} /> : null}

      {activeTab ? (
        <div className='mt-2'>
          {tabs.length > 1 ? (
            <div className='d-flex flex-wrap gap-1 mb-1'>
              {tabs.map((tab) => {
                const isActive = tab.axis === activeTab.axis
                return (
                  <button
                    key={tab.axis}
                    type='button'
                    className='btn btn-sm px-2 py-1'
                    onClick={() => setActiveAxis(tab.axis)}
                    style={{
                      fontSize: '0.72rem',
                      borderRadius: 999,
                      border: `1px solid ${borderColor}`,
                      backgroundColor: isActive ? activeTabBg : 'transparent',
                      color: isActive ? activeTabColor : mutedColor,
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    {tab.label}
                  </button>
                )
              })}
            </div>
          ) : null}
          <AttendanceV2ListSection
            key={activeTab.axis}
            title={tabs.length > 1 ? '' : activeTab.label}
            columns={activeTab.columns}
            rows={activeTab.rows}
            theme={theme}
          />
        </div>
      ) : null}

      {lowestClasses.length ? (
        <AttendanceV2ListSection
          title={intl.formatMessage(
            {id: 'CHATBOT.ATTENDANCE.REPORT_LOWEST_CLASSES'},
            {count: lowestClasses.length}
          )}
          columns={LOWEST_CLASS_COLUMNS}
          rows={lowestClasses as Record<string, unknown>[]}
          theme={theme}
        />
      ) : null}

      {belowThresholdStudents.length ? (
        <AttendanceV2ListSection
          title={intl.formatMessage(
            {id: 'CHATBOT.ATTENDANCE.REPORT_BELOW_THRESHOLD'},
            {threshold: threshold ?? '', count: belowThresholdStudents.length}
          )}
          columns={BELOW_THRESHOLD_COLUMNS}
          rows={belowThresholdStudents as Record<string, unknown>[]}
          theme={theme}
        />
      ) : null}
    </div>
  )
}

export default AttendanceV2ComprehensiveReportView
